// LocationPage.jsx

import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useSearchParams, useParams } from "react-router-dom";
import "./LocationPage.css";
import { motion, AnimatePresence } from "framer-motion";
import LocationGridLayout from "./Layout/LocationGridLayout";
import {
  ArrowLeft,
  Image,
  Video,
  Film,
  MoreHorizontal,
  PlusCircle,
  MinusCircle,
  FolderPlus,
  FolderMinus,
  AlertTriangle,
  Trash2,
} from "lucide-react";

// ==================== TEMPORARY LOCALSTORAGE AUTH ====================
// ⚠️⚠️⚠️ MARK FOR LATER REMOVAL ⚠️⚠️⚠️
// This localStorage-based "auth" is a TEMPORARY bridge for the demo.
// It will be replaced with single sign-on JWT / OAuth token-based auth
// (httpOnly cookies + short-lived access tokens) once the backend auth
// service is integrated across all Kleido domains and subdomains.
//
// TODO: Replace ensureUserLoggedin() with a secure token exchange:
//   received userid → backend /auth/exchange → JWT in httpOnly cookie
// ==================== TEMPORARY LOCALSTORAGE AUTH ====================

const ensureUserLoggedin = (receivedUserid) => {
  try {
    const raw = localStorage.getItem("userLoggedin");
    if (!raw) {
      // First entry from external app — seed localStorage
      const entry = { userid: receivedUserid, timestamp: Date.now() };
      localStorage.setItem("userLoggedin", JSON.stringify(entry));
      console.log("✅ [LocationPage] Seeded userLoggedin →", receivedUserid);
    } else {
      // Already exists — update only if the userid has changed
      const parsed = JSON.parse(raw);
      const existingId = parsed?.userid || parsed?.id || parsed?.userId;
      if (existingId !== receivedUserid) {
        const updated = { ...parsed, userid: receivedUserid, updatedAt: Date.now() };
        localStorage.setItem("userLoggedin", JSON.stringify(updated));
        console.log("🔄 [LocationPage] Updated userLoggedin →", receivedUserid);
      }
    }
  } catch (err) {
    console.error("❌ [LocationPage] localStorage error:", err);
  }
};

// ==================== HELPER: DETERMINE FETCH MODE ====================

const determineFetchMode = (userid, locationid, countryid, locationname, year) => {
  if (userid && locationid && !countryid && !locationname && !year)
    return "userid-locationid";
  if (locationid && !userid && !countryid && !locationname && !year)
    return "locationid-only";
  if (countryid && locationid && !userid && !locationname && !year)
    return "countryid-locationid";
  if (locationname && year && !userid && !countryid && !locationid)
    return "locationname-year";
  if (userid && locationname && year && !countryid && !locationid)
    return "userid-locationname-year";
  if (userid && countryid && locationid && !locationname && !year)
    return "userid-countryid-locationid";
  return "unknown";
};

// ==================== HELPER: BUILD EVENT DATA ====================

const buildLocationData = (fetchMode, userid, countryid, locationid, locationname, year) => {
  switch (fetchMode) {
    case "userid-locationid":
      return {
        locationId:   locationid,
        locationName: `Location ${locationname}`,
        userId:    userid,
        locationType: "User Location",
        year:      new Date().getFullYear(),
      };
    case "locationid-only":
      return {
        locationId:   locationid,
        locationName: `Location ${locationname}`,
        locationType: "Public Location",
        year:      new Date().getFullYear(),
      };
    case "countryid-locationid":
      return {
        locationId:   locationname,
        locationName: `Society Location ${locationname}`,
        countryId: countryid,
        locationType: "Society Location",
        year:      new Date().getFullYear(),
      };
    case "locationname-year":
      return {
        locationName: locationname || "Unknown Location",
        year:      parseInt(year || "2025"),
        locationType: "Named Location",
      };
    case "userid-locationname-year":
      return {
        locationName: locationname || "Unknown Location",
        year:      parseInt(year || "2025"),
        userId:    userid,
        locationType: "User Named Location",
      };
    case "userid-countryid-locationid":
      return {
        locationId:   locationid,
        locationName: `Country Location ${countryid}`,
        userId:    userid,
        countryId: countryid,
        locationType: "User Society Location",
        year:      new Date().getFullYear(),
      };
    default:
      return null;
  }
};

// ==================== MORE DROPDOWN COMPONENT ====================

const MoreDropdown = ({ onAction }) => {
  const [open, setOpen] = useState(false);
  const dropdownRef     = useRef(null);

  const menuItems = [
    { id: "add-content",            label: "Add Content",             icon: PlusCircle,   danger: false },
    { id: "remove-content",         label: "Remove Content",          icon: MinusCircle,  danger: false },
    { id: "create-content-group",   label: "Create Content Group",    icon: FolderPlus,   danger: false },
    { id: "remove-content-group",   label: "Remove Content Group",    icon: FolderMinus,  danger: false },
    { id: "divider" },
    { id: "report-abuse",           label: "Report Abuse",            icon: AlertTriangle, danger: true },
    { id: "request-remove-content", label: "Request to Remove Content", icon: Trash2,    danger: true },
  ];

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <div ref={dropdownRef} style={{ position: "relative" }}>
      <button
        onClick={() => setOpen((prev) => !prev)}
        aria-label="More options"
        aria-haspopup="true"
        aria-expanded={open}
        style={{
          display:         "flex",
          alignItems:      "center",
          gap:             "6px",
          padding:         "8px 14px",
          borderRadius:    "8px",
          border:          "1px solid var(--border-color, rgb(52,51,51))",
          background:      open
            ? "rgba(255,255,255,0.08)"
            : "var(--card-bg, rgb(4,4,4))",
          color:           "white",
          cursor:          "pointer",
          fontSize:        "14px",
          fontWeight:      500,
          transition:      "background 0.2s",
          whiteSpace:      "nowrap",
        }}
      >
        <MoreHorizontal size={18} />
        More
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -8, scale: 0.96 }}
            animate={{ opacity: 1, y: 0,  scale: 1    }}
            exit={{   opacity: 0, y: -8, scale: 0.96 }}
            transition={{ duration: 0.18, ease: "easeOut" }}
            style={{
              position:      "absolute",
              top:           "calc(100% + 8px)",
              right:         0,
              zIndex:        1000,
              minWidth:      "220px",
              background:    "rgb(14,14,14)",
              border:        "1px solid var(--border-color, rgb(52,51,51))",
              borderRadius:  "10px",
              boxShadow:     "0 8px 32px rgba(0,0,0,0.6)",
              overflow:      "hidden",
              padding:       "6px 0",
            }}
          >
            {menuItems.map((item) => {
              if (item.id === "divider") {
                return (
                  <div
                    key="divider"
                    style={{
                      height:     "1px",
                      background: "var(--border-color, rgb(52,51,51))",
                      margin:     "6px 0",
                    }}
                  />
                );
              }
              const Icon = item.icon;
              return (
                <button
                  key={item.id}
                  onClick={() => {
                    onAction && onAction(item.id);
                    setOpen(false);
                  }}
                  style={{
                    display:      "flex",
                    alignItems:   "center",
                    gap:          "10px",
                    width:        "100%",
                    padding:      "10px 16px",
                    background:   "transparent",
                    border:       "none",
                    color:        item.danger ? "#ff5c5c" : "rgba(255,255,255,0.85)",
                    cursor:       "pointer",
                    fontSize:     "14px",
                    fontWeight:   400,
                    textAlign:    "left",
                    transition:   "background 0.15s, color 0.15s",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = item.danger
                      ? "rgba(255,92,92,0.08)"
                      : "rgba(255,255,255,0.06)";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = "transparent";
                  }}
                >
                  <Icon size={16} />
                  {item.label}
                </button>
              );
            })}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

// ==================== MAIN COMPONENT ====================

const LocationPage = () => {
  const navigate = useNavigate();
  const { suserId } = useParams();

  const [searchParams] = useSearchParams();

  const userid       = searchParams.get("userid");
  const countryid    = searchParams.get("countryid");
  const locationid   = searchParams.get("locationid");
  const locationname = searchParams.get("locationname");
  const year         = searchParams.get("year");

  const fetchMode = determineFetchMode(userid, countryid, locationid, locationname, year);

  const [showLocationPage, setShowLocationPage] = useState(true);
  const [activeTab,     setActiveTab]     = useState("images");
  const [pageState,     setPageState]     = useState({
    fetchMode: "unknown",
    locationData: null,
    loading:   true,
    error:     null,
  });

  const tabItems = [
    { id: "images", label: "Images", icon: Image },
    { id: "reels",  label: "Reels",  icon: Film  },
    { id: "videos", label: "Videos", icon: Video },
  ];

  // ==================== SEED LOCALSTORAGE ON ENTRY ====================
  // ⚠️ TEMPORARY — mark for later removal (see header comment)
  useEffect(() => {
    const effectiveUserid = userid || suserId;
    if (effectiveUserid) {
      ensureUserLoggedin(effectiveUserid);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ==================== FETCH MODE + LOCATION DATA ====================
  useEffect(() => {
    if (fetchMode === "unknown") {
      setPageState({
        fetchMode: "unknown",
        locationData: null,
        loading:   false,
        error:
          "Invalid URL parameters. Valid combinations:\n" +
          "• ?locationid=xyz\n" +
          "• ?userid=abc&locationid=xyz\n" +
          "• ?countryid=soc&countryid=xyz\n" +
          "• ?locationname=xyz&year=2025\n" +
          "• ?userid=abc&locationname=xyz&year=2025\n" +
          "• ?userid=abc&societyid=soc&locationid=xyz",
      });
      return;
    }

    const locationData = buildLocationData(
      fetchMode, userid, countryid, locationid, locationname, year
    );

    setPageState({ fetchMode, locationData, loading: false, error: null });
  }, [fetchMode, userid, countryid, locationid, locationname, year]);

  const handleGoBack = () => {
    setShowLocationPage(false);
    setTimeout(() => navigate(-1), 300);
  };

  const handleMoreAction = (actionId) => {
    console.log("[LocationPage] More action triggered:", actionId);
    // Hook your action handlers here
  };

  // ── Loading ──────────────────────────────────────────────
  if (pageState.loading) {
    return (
      <div className="locationpage-loading-container">
        <div className="locationpage-spinner" />
        <p style={{ color: "white" }}>Loading location...</p>
      </div>
    );
  }

  // ── Error ────────────────────────────────────────────────
  if (pageState.error) {
    return (
      <div
        className="locationpage-error-container"
        style={{
          background: "var(--card-bg, rgb(4,4,4))",
          color:      "white",
          minHeight:  "100vh",
          display:    "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <div className="locationpage-error-content">
          <h2>⚠️ Error Loading Location</h2>
          <pre style={{ color: "rgba(255,255,255,0.7)", fontSize: "13px" }}>
            {pageState.error}
          </pre>
          <button
            className="locationpage-error-btn"
            onClick={() => navigate(-1)}
            style={{
              marginTop:    "16px",
              padding:      "10px 24px",
              background:   "transparent",
              border:       "1px solid var(--border-color, rgb(52,51,51))",
              borderRadius: "8px",
              color:        "white",
              cursor:       "pointer",
              fontSize:     "14px",
            }}
          >
            ← Go Back
          </button>
        </div>
      </div>
    );
  }

  // ── Main Render ──────────────────────────────────────────
  return (
    <div
      style={{
        background: "var(--card-bg, rgb(4,4,4))",
        minHeight:  "100vh",
        color:      "white",
        marginTop:  0,
      }}
    >
      {showLocationPage && (
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0  }}
          exit={{    opacity: 0, y: -12 }}
          transition={{ duration: 0.4, ease: "easeOut" }}
          style={{ paddingTop: "24px" }}
        >
          {/* ── Central 80% Container ─────────────────────── */}
          <div
            style={{
              width:     "80%",
              margin:    "0 auto",
              boxSizing: "border-box",
            }}
          >
            {/* ── Top Bar ───────────────────────────────────── */}
            <div
              style={{
                display:        "flex",
                alignItems:     "center",
                justifyContent: "space-between",
                marginBottom:   "20px",
                flexWrap:       "wrap",
                gap:            "12px",
              }}
            >
              {/* Left: Go Back + Title */}
              <div style={{ display: "flex", alignItems: "center", gap: "16px" }}>
                <button
                  onClick={handleGoBack}
                  aria-label="Go back"
                  style={{
                    display:      "flex",
                    alignItems:   "center",
                    gap:          "6px",
                    padding:      "8px 14px",
                    borderRadius: "8px",
                    border:       "1px solid var(--border-color, rgb(52,51,51))",
                    background:   "transparent",
                    color:        "rgba(255,255,255,0.85)",
                    cursor:       "pointer",
                    fontSize:     "14px",
                    fontWeight:   500,
                    transition:   "background 0.2s, color 0.2s",
                    whiteSpace:   "nowrap",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = "rgba(255,255,255,0.07)";
                    e.currentTarget.style.color      = "white";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = "transparent";
                    e.currentTarget.style.color      = "rgba(255,255,255,0.85)";
                  }}
                >
                  <ArrowLeft size={16} />
                  Go Back
                </button>
              </div>

              {/* Right: Tab Buttons + More */}
              <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                {/* Tab Buttons */}
                <div
                  style={{
                    display:      "flex",
                    alignItems:   "center",
                    gap:          "4px",
                    background:   "rgba(255,255,255,0.04)",
                    border:       "1px solid var(--border-color, rgb(52,51,51))",
                    borderRadius: "10px",
                    padding:      "4px",
                  }}
                >
                  {tabItems.map(({ id, label, icon: Icon }) => {
                    const isActive = activeTab === id;
                    return (
                      <button
                        key={id}
                        onClick={() => setActiveTab(id)}
                        aria-label={label}
                        aria-pressed={isActive}
                        style={{
                          display:      "flex",
                          alignItems:   "center",
                          gap:          "6px",
                          padding:      "7px 14px",
                          borderRadius: "7px",
                          border:       "none",
                          background:   isActive
                            ? "rgba(255,255,255,0.12)"
                            : "transparent",
                          color:        isActive
                            ? "white"
                            : "rgba(255,255,255,0.5)",
                          cursor:     "pointer",
                          fontSize:   "13px",
                          fontWeight: isActive ? 600 : 400,
                          transition: "background 0.2s, color 0.2s",
                          whiteSpace: "nowrap",
                        }}
                        onMouseEnter={(e) => {
                          if (!isActive) {
                            e.currentTarget.style.background = "rgba(255,255,255,0.06)";
                            e.currentTarget.style.color      = "rgba(255,255,255,0.8)";
                          }
                        }}
                        onMouseLeave={(e) => {
                          if (!isActive) {
                            e.currentTarget.style.background = "transparent";
                            e.currentTarget.style.color      = "rgba(255,255,255,0.5)";
                          }
                        }}
                      >
                        <Icon size={15} />
                        {label}
                      </button>
                    );
                  })}
                </div>

                {/* More Dropdown */}
                <MoreDropdown onAction={handleMoreAction} />
              </div>
            </div>

            {/* ── Divider ──────────────────────────────────── */}
            <div
              style={{
                height:       "1px",
                background:   "var(--border-color, rgb(52,51,51))",
                marginBottom: "24px",
              }}
            />

            {/* ── Content Grid ─────────────────────────────── */}
            <LocationGridLayout
              suserid={suserId}
              fetchMode={pageState.fetchMode}
              locationData={pageState.locationData}
              contentType={activeTab}
            />
          </div>
        </motion.div>
      )}

      {/* ── Footer ─────────────────────────────────────────── */}
      <footer
        style={{
          borderTop:  "1px solid var(--border-color, rgb(52,51,51))",
          padding:    "28px 16px",
          marginTop:  "48px",
          background: "var(--card-bg, rgb(4,4,4))",
        }}
      >
        <div
          style={{
            width:     "80%",
            margin:    "0 auto",
            textAlign: "center",
            fontSize:  "13px",
            color:     "rgba(255,255,255,0.35)",
          }}
        >
          <p style={{ margin: 0 }}>
            Kleido AI, an Intelligent social Platform. © 2025 Initiumnet Technology Pvt Limited, all rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default LocationPage;
